import type { I18n } from '@lingui/core';
import { t } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';

import { useRT3Presence, type RT3PresenceState } from '../hooks/useRT3Presence';
import { formatRelativeTime } from '../i18n/format';

interface UserPresenceDotProps {
  userID: string;

  now?: () => number;
}

function dotAttr(
  state: RT3PresenceState | undefined,
  lastSeenAt: number | undefined,
  now: number,
): string {
  if (state === 'online') return 'online';
  if (state === 'offline' || state === undefined) return 'offline';

  return 'recently-active';
}

function lastSeenLabel(i18n: I18n, lastSeenAt: number, now: number): string {
  const diffMs = now - lastSeenAt;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return t(i18n)`Active just now`;
  const lastSeen = formatRelativeTime(i18n, -diffMin, 'minute', { numeric: 'always' });
  return t(i18n)`Last active ${lastSeen}`;
}

export function UserPresenceDot({ userID, now }: UserPresenceDotProps) {
  const { i18n } = useLingui();
  const entry = useRT3Presence(userID);
  const nowFn = now ?? Date.now;
  const currentTime = nowFn();
  const attr = dotAttr(entry?.state, entry?.lastSeenAt, currentTime);
  const lastSeenAt = entry?.lastSeenAt ?? 0;
  const tooltip =
    attr === 'online'
      ? t(i18n)`Online`
      : attr === 'offline'
        ? t(i18n)`Offline`
        : lastSeenLabel(i18n, lastSeenAt, currentTime);

  return (
    <span
      className={`user-presence-dot user-presence-dot-${attr}`}
      data-user-presence-dot={attr}
      data-user-last-seen={lastSeenAt}
      data-user-cursor-user={userID}
      title={tooltip}
      role="status"
      aria-label={tooltip}
    />
  );
}
